import { Dialog as BaseDialog } from '@base-ui/react/dialog';
import { X } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';
import { useI18n } from '../../i18n/i18n';
import { cn } from '../../lib/cn';

export const Dialog = BaseDialog.Root;

export function DialogTrigger({ children }: { children: ReactElement }) {
  return <BaseDialog.Trigger render={children} />;
}

export function DialogClose({ children }: { children: ReactElement }) {
  return <BaseDialog.Close render={children} />;
}

export const BACKDROP =
  'fixed inset-0 z-50 bg-overlay backdrop-blur-[2px] transition-opacity data-[ending-style]:opacity-0 data-[starting-style]:opacity-0';

export const POPUP =
  'fixed top-1/2 left-1/2 z-50 flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col rounded-[12px] border border-border bg-card text-foreground shadow-modal outline-none transition-[opacity,transform] data-[ending-style]:scale-95 data-[ending-style]:opacity-0 data-[starting-style]:scale-95 data-[starting-style]:opacity-0';

interface DialogContentProps {
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  /** Кнопки внизу, слева: основная первой, затем «Отмена» (как в прототипе). */
  footer?: ReactNode;
  /** Ширина: `sm` — подтверждения и короткие формы, `lg` — экспорт, версии, превью. */
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const WIDTHS = { sm: 'max-w-[400px]', md: 'max-w-[460px]', lg: 'max-w-2xl' };

/**
 * Модальный диалог. Рендерится порталом в `body`: панели на холсте анимируются `transform`-ом,
 * и `position: fixed` внутри них считался бы от панели (docs/saas/screens/схема.md).
 */
export function DialogContent({
  title,
  description,
  children,
  footer,
  size = 'md',
  className,
}: DialogContentProps) {
  const { t } = useI18n();
  return (
    <BaseDialog.Portal>
      <BaseDialog.Backdrop className={BACKDROP} />
      <BaseDialog.Popup className={cn(POPUP, WIDTHS[size], className)}>
        <div className="flex items-start gap-4 px-6 pt-6">
          <div className="min-w-0 flex-1 space-y-1">
            <BaseDialog.Title className="text-[15px] font-semibold">{title}</BaseDialog.Title>
            {description !== undefined && (
              <BaseDialog.Description className="text-sm text-muted-foreground">
                {description}
              </BaseDialog.Description>
            )}
          </div>
          <BaseDialog.Close
            aria-label={t('common.close')}
            className="-mt-1.5 -mr-2 inline-flex size-7 items-center justify-center rounded-md text-hint outline-none hover:bg-tint hover:text-foreground focus-visible:ring-1 focus-visible:ring-primary"
          >
            <X className="size-4" />
          </BaseDialog.Close>
        </div>
        {children !== undefined && (
          <div className="min-h-0 flex-1 overflow-y-auto px-6 pt-3.5">{children}</div>
        )}
        <div className="flex gap-2 px-6 pt-3.5 pb-6">{footer}</div>
      </BaseDialog.Popup>
    </BaseDialog.Portal>
  );
}
